import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaLock, FaUnlock, FaChevronLeft, FaMoneyBillWave, FaClock, FaChevronRight, FaTimes, FaImage } from 'react-icons/fa';
import DashboardLayout from '../../components/layout/DashboardLayout';
import API_BASE_URL from '../../config/api';

const MonthSelection = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user'));
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedMonthForPayment, setSelectedMonthForPayment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [paymentSlip, setPaymentSlip] = useState(null);

  useEffect(() => {
    if (!user || !user.id) { navigate('/login'); return; }
    const fetchMonths = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get_course_months.php?course_id=${courseId}&user_id=${user.id}`);
        const result = await response.json();
        if (result.status === 'success') setMonths(result.data);
      } catch (error) {
        console.error("Error fetching months:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMonths();
  }, [courseId, user?.id, navigate]);

  if (!user || !user.id) return null;

  const handleMonthClick = (month) => {
    if (month.enrollment_status === 'active') {
      navigate(`/student/materials/${month.id}`);
    } else if (month.payment_status === 'pending') {
      alert("Your payment is pending approval. Please wait.");
    } else {
      setSelectedMonthForPayment(month);
      setShowPaymentModal(true);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentSlip) { alert("Please select a payment slip image."); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('user_id', user.id);
    formData.append('course_id', courseId);
    formData.append('month_id', selectedMonthForPayment.id);
    formData.append('amount', selectedMonthForPayment.price || '2000');
    formData.append('slip', paymentSlip);
    try {
      const response = await fetch(`${API_BASE_URL}/submit_payment.php`, { method: 'POST', body: formData });
      const result = await response.json();
      if (result.status === 'success') {
        alert(result.message);
        setShowPaymentModal(false);
        setPaymentSlip(null);
        const fr = await fetch(`${API_BASE_URL}/get_course_months.php?course_id=${courseId}&user_id=${user.id}`);
        const fResult = await fr.json();
        if (fResult.status === 'success') setMonths(fResult.data);
      } else { alert(result.message); }
    } catch (error) {
      alert("Submission failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <DashboardLayout title="Monthly Enrollment">
      <div className="space-y-4 sm:space-y-6">
        <button onClick={() => navigate('/courses')} className="flex items-center gap-1.5 text-slate-400 hover:text-orange-500 font-bold text-[10px] uppercase tracking-widest transition-colors">
          <FaChevronLeft size={10} /> Back to Courses
        </button>

        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-7 text-white shadow-lg">
          <h2 className="text-base sm:text-xl font-black mb-1">Select Your Month</h2>
          <p className="text-slate-400 text-xs font-medium">Pay for the month you want to access.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            {months.map((month) => (
              <div
                key={month.id}
                onClick={() => handleMonthClick(month)}
                className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 sm:p-5 flex flex-col hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer border-b-4 border-b-transparent hover:border-b-orange-500 group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-50 text-slate-800 rounded-lg sm:rounded-xl flex items-center justify-center font-black text-sm sm:text-base shadow-inner group-hover:bg-orange-500 group-hover:text-white transition-colors">
                    {month.month_name.substring(0, 3)}
                  </div>
                  {month.enrollment_status === 'active' ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-600 rounded-full text-[8px] font-black uppercase tracking-widest">
                      <FaUnlock size={8} /> {month.is_grace_period ? 'Unlocked' : 'Enrolled'}
                    </span>
                  ) : month.payment_status === 'pending' ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-[8px] font-black uppercase tracking-widest">
                      <FaClock size={8} /> Pending
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-slate-50 text-slate-400 rounded-full text-[8px] font-black uppercase tracking-widest">
                      <FaLock size={8} /> Locked
                    </span>
                  )}
                </div>

                <h3 className="text-xs sm:text-sm font-bold text-slate-800 mb-0.5">{month.month_name}</h3>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mb-3">Materials & Recordings</p>

                <div className="mt-auto pt-3 border-t border-slate-50 flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">Price</span>
                    <span className="text-sm font-black text-slate-800">LKR {month.price || '2000'}</span>
                  </div>
                  <div className="p-1.5 bg-slate-50 rounded-lg text-slate-400 group-hover:bg-orange-500 group-hover:text-white transition-all">
                    {month.enrollment_status === 'active' ? <FaChevronRight size={11} /> : <FaMoneyBillWave size={11} />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5 sm:p-7 shadow-2xl">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-base sm:text-xl font-black text-slate-800 mb-0.5">Unlock {selectedMonthForPayment?.month_name}</h3>
                <p className="text-slate-400 text-[9px] font-medium uppercase tracking-widest">Upload your payment proof</p>
              </div>
              <button onClick={() => setShowPaymentModal(false)} className="text-slate-300 hover:text-slate-500 transition-colors p-1">
                <FaTimes size={20} />
              </button>
            </div>

            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div className="bg-orange-50 rounded-xl p-3 border border-orange-100">
                <div className="flex justify-between items-center mb-0.5">
                  <span className="text-[9px] font-black text-orange-400 uppercase tracking-[0.2em]">Payable Amount</span>
                  <span className="text-[9px] font-black text-orange-600 bg-white px-2 py-0.5 rounded shadow-sm">BANK TRANSFER</span>
                </div>
                <p className="text-xl font-black text-orange-500">LKR {selectedMonthForPayment?.price || '2000'}</p>
              </div>

              <div className="space-y-1.5">
                <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest">Payment Slip (Image)</label>
                <div className="relative group">
                  <input type="file" accept="image/*" onChange={(e) => setPaymentSlip(e.target.files[0])} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                  <div className={`w-full py-6 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 transition-all ${paymentSlip ? 'border-green-400 bg-green-50/30' : 'border-slate-200 bg-slate-50 group-hover:border-orange-300'}`}>
                    <div className={`p-2 rounded-xl ${paymentSlip ? 'bg-green-100 text-green-600' : 'bg-white text-slate-300 shadow-sm'}`}>
                      <FaImage size={18} />
                    </div>
                    <span className={`text-[10px] font-bold ${paymentSlip ? 'text-green-600' : 'text-slate-400'}`}>
                      {paymentSlip ? paymentSlip.name : 'Select or Drop Slip Image'}
                    </span>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={uploading} className={`w-full py-3.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow ${uploading ? 'bg-slate-100 text-slate-400' : 'bg-orange-500 text-white hover:bg-orange-600 active:scale-95'}`}>
                {uploading ? <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div> : <>Unlock Now <FaChevronRight size={10} /></>}
              </button>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default MonthSelection;
